<template>
  <div class="xiaoguo-square">
    <!-- 操场形状留言墙 -->
    <div class="message-wall-container">
      <div class="message-wall">
        <div class="danmaku-container" ref="danmakuContainer">
          <div 
            v-for="(message, index) in messages" 
            :key="message.id || index" 
            class="message-item"
            :style="{
              top: `${message.top}px`,
              animationDuration: `${message.duration}s`,
              animationDelay: `${message.delay}s`
            }"
          >
            {{ message.content }}
          </div>
        </div>
      </div>
      
      <div class="input-area">
        <div class="input-wrapper">
          <input 
            type="text" 
            v-model="newMessage" 
            placeholder="留言墙功能测试中..."
            class="message-input"
            maxlength="50"
            @keyup.enter="addMessage"
          />
          <span class="char-count">{{ newMessage.length }}/50</span>
        </div>
        <button class="send-button" @click="addMessage" :disabled="isSubmitting">
          <span class="button-text">{{ isSubmitting ? '发送中...' : '发送' }}</span>
        </button>
        <button class="my-messages-button" @click="toggleMyMessages">
          {{ isShowingMyMessages ? '返回' : '我的' }}
        </button>
      </div>
    </div>

    <!-- 小果笔记&Blog区域标题 -->
    <div class="notes-section">
      <h2 class="notes-title">小果笔记&Blog</h2>
      <div class="blog-card" @click="goToBlog">
        <div class="blog-content">
          <div class="blog-avatar">
            <img src="@/assets/Blog.png" alt="Blog Logo">
          </div>
          <div class="blog-info">
            <h3 class="blog-title">小果Blog</h3>
            <p class="blog-description">小果的个人博客，包含各类技术总结</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 我的留言列表 -->
    <div v-if="isShowingMyMessages" class="my-messages-container">
      <h3 class="my-messages-title">我的留言</h3>
      <div v-if="myMessages.length === 0" class="no-messages">
        您还没有发送过留言
      </div>
      <div v-else class="my-messages-list">
        <div v-for="message in myMessages" :key="message.id" class="my-message-item">
          <div class="message-content">
            <span class="approval-tag" :class="{ 'approved': message.approved, 'pending': !message.approved }">
              {{ message.approved ? '已审核' : '审核中' }}
            </span>
            <span>{{ message.content }}</span>
            <span class="message-date">{{ formatDate(message.createdAt) }}</span>
          </div>
          <div class="message-actions">
            <button class="action-button edit" @click="editMessage(message)">
              <i class="el-icon-edit"></i> 修改
            </button>
            <button class="action-button delete" @click="confirmDeleteMessage(message)">
              <i class="el-icon-delete"></i> 删除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑留言对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      title="编辑留言"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="edit-message-form">
        <el-input
          v-model="editingMessage.content"
          type="textarea"
          :rows="4"
          placeholder="请输入留言内容"
          maxlength="50"
          show-word-limit
        ></el-input>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="updateMessageContent" :loading="isUpdating">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 删除确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="确认删除"
      width="400px"
    >
      <p>确定要删除这条留言吗？此操作不可恢复。</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button type="danger" @click="deleteMessageConfirmed" :loading="isDeleting">确认删除</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { getMessageWallMessages, getUserMessages, sendMessage, updateMessage, deleteMessage } from '@/services/messageWallService';
import { ElMessage, ElMessageBox } from 'element-plus';

// 留言数据
const messages = ref([]);
const myMessages = ref([]);
const danmakuContainer = ref(null);
const newMessage = ref('');
const isSubmitting = ref(false);
const isShowingMyMessages = ref(false);
const editDialogVisible = ref(false);
const deleteDialogVisible = ref(false);
const isUpdating = ref(false);
const isDeleting = ref(false);
const editingMessage = ref({});
const messageToDelete = ref(null);
let containerWidth = 0;
const containerHeight = 160;

// 跳转到博客网站
const goToBlog = () => {
  window.open('https://www.schedulebegin.online/', '_blank');
};

// 声明为全局变量，以便在onBeforeUnmount中使用
const handleResize = () => {
  if (danmakuContainer.value) {
    containerWidth = danmakuContainer.value.offsetWidth;
  }
};

// 获取可用的垂直空间并避免弹幕重叠
const getAvailableYPosition = (messageHeight = 40) => {
  const usedPositions = messages.value.map(m => m.top);
  const possiblePositions = [];
  
  // 划分可用空间
  const steps = 5;
  const stepSize = (containerHeight - messageHeight) / steps;
  
  for (let i = 0; i < steps; i++) {
    possiblePositions.push(i * stepSize + 10);
  }
  
  // 尝试找到一个没有被占用的位置
  for (const pos of possiblePositions) {
    if (!usedPositions.some(usedPos => Math.abs(usedPos - pos) < messageHeight)) {
      return pos;
    }
  }
  
  // 如果所有位置都被占用，随机选择一个位置
  return Math.floor(Math.random() * (containerHeight - messageHeight - 20)) + 10;
};

// 从API获取留言数据
const fetchMessages = async () => {
  try {
    const response = await getMessageWallMessages();
    console.log('API返回数据:', response);
    
    if (response && response.code === 200 && response.data && Array.isArray(response.data.data)) {
      // 将API返回的数据转换为弹幕数据结构
      initDanmakuWithApiData(response.data.data);
    } else {
      console.error('API返回数据格式不正确:', response);
      ElMessage.warning('获取留言数据格式不正确');
    }
  } catch (error) {
    console.error('获取留言失败:', error);
    ElMessage.error('获取留言失败，请稍后再试');
  }
};

// 获取我的留言
const fetchMyMessages = async () => {
  try {
    const response = await getUserMessages();
    console.log('我的留言数据:', response);
    
    if (response && response.code === 200 && response.data && Array.isArray(response.data.data)) {
      myMessages.value = response.data.data;
    } else {
      console.error('获取我的留言数据格式不正确:', response);
      ElMessage.warning('获取我的留言数据格式不正确');
    }
  } catch (error) {
    console.error('获取我的留言失败:', error);
    ElMessage.error('获取我的留言失败，请稍后再试');
  }
};

// 切换显示我的留言
const toggleMyMessages = () => {
  isShowingMyMessages.value = !isShowingMyMessages.value;
  if (isShowingMyMessages.value) {
    fetchMyMessages();
  }
};

// 使用API数据初始化弹幕
const initDanmakuWithApiData = (apiMessages) => {
  if (!danmakuContainer.value) return;
  
  containerWidth = danmakuContainer.value.offsetWidth;
  
  // 清空现有留言，避免重复
  messages.value = [];
  
  // 计算垂直位置的间隔
  const verticalSpacing = containerHeight / Math.min(apiMessages.length, 5);
  
  // 为每条留言计算一个错开的延迟时间和垂直位置
  apiMessages.forEach((message, index) => {
    const verticalPosition = (index % 5) * verticalSpacing + 10; // 确保留言分布在不同高度
    
    messages.value.push({
      ...message,
      top: verticalPosition,
      duration: 15 + Math.random() * 5, // 调整动画持续时间
      delay: index * 0.2 // 进一步减少延迟时间
    });
  });
};

// 添加新留言
const addMessage = async () => {
  if (newMessage.value.trim() && !isSubmitting.value) {
    try {
      isSubmitting.value = true;
      
      // 调用API发送留言
      const response = await sendMessage(newMessage.value);
      console.log('发送留言响应:', response);
      
      // 检查响应结构
      if (response && response.code === 200 && response.data) {
        // 计算一个不同的垂直位置，避免与现有留言重叠
        const existingPositions = messages.value.map(m => m.top);
        let newPosition = 20; // 默认位置
        
        // 尝试找到一个空闲的位置
        for (let i = 0; i < 5; i++) {
          const testPosition = i * (containerHeight / 5) + 10;
          if (!existingPositions.some(pos => Math.abs(pos - testPosition) < 30)) {
            newPosition = testPosition;
            break;
          }
        }
        
        // 创建新弹幕
        const newDanmaku = {
          ...response.data,
          top: newPosition,
          duration: 15 + Math.random() * 5, // 调整动画持续时间
          delay: 0
        };
        
        // 添加到弹幕数组
        messages.value.push(newDanmaku);
        newMessage.value = '';
        ElMessage.success('您的留言已发出，审核通过后将会公共展示！');
      } else {
        console.error('发送留言返回数据格式不正确:', response);
        ElMessage.warning('留言发送成功，但返回数据格式不正确');
      }
    } catch (error) {
      console.error('发送留言失败:', error);
      ElMessage.error('发送留言失败，请稍后再试');
    } finally {
      isSubmitting.value = false;
    }
  }
};

// 编辑留言
const editMessage = (message) => {
  editingMessage.value = { ...message };
  editDialogVisible.value = true;
};

// 更新留言内容
const updateMessageContent = async () => {
  if (!editingMessage.value.content.trim()) {
    ElMessage.warning('留言内容不能为空');
    return;
  }

  try {
    isUpdating.value = true;
    const response = await updateMessage(editingMessage.value.id, editingMessage.value.content);
    
    if (response && response.code === 200) {
      ElMessage.success('留言更新成功');
      // 更新本地数据
      const index = myMessages.value.findIndex(m => m.id === editingMessage.value.id);
      if (index !== -1) {
        myMessages.value[index] = { ...myMessages.value[index], ...response.data };
      }
      editDialogVisible.value = false;
    } else {
      ElMessage.error('留言更新失败');
    }
  } catch (error) {
    console.error('更新留言失败:', error);
    ElMessage.error('更新留言失败，请稍后再试');
  } finally {
    isUpdating.value = false;
  }
};

// 确认删除留言
const confirmDeleteMessage = (message) => {
  messageToDelete.value = message;
  deleteDialogVisible.value = true;
};

// 删除留言
const deleteMessageConfirmed = async () => {
  if (!messageToDelete.value) return;
  
  try {
    isDeleting.value = true;
    const response = await deleteMessage(messageToDelete.value.id);
    
    if (response && response.code === 200) {
      ElMessage.success('留言删除成功');
      // 从本地数据中移除
      myMessages.value = myMessages.value.filter(m => m.id !== messageToDelete.value.id);
      deleteDialogVisible.value = false;
    } else {
      ElMessage.error('留言删除失败');
    }
  } catch (error) {
    console.error('删除留言失败:', error);
    ElMessage.error('删除留言失败，请稍后再试');
  } finally {
    isDeleting.value = false;
  }
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// 组件挂载时初始化弹幕
onMounted(() => {
  fetchMessages();
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize);
});

// 组件卸载时清除事件监听
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.xiaoguo-square {
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  max-width: 100%;
}

.message-wall-container {
  margin: 0 auto;
  width: 80%;
}

/* 操场形状的留言墙 */
.message-wall {
  background-color: #f5f7fa;
  /* 创建半圆形两端的操场形状 */
  border-radius: 80px;
  padding: 20px 40px;
  margin-bottom: 30px;
  height: 160px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  /* 多层阴影创建立体感 */
  box-shadow: 
    0 10px 20px rgba(0, 0, 0, 0.08),
    0 6px 6px rgba(0, 0, 0, 0.12),
    inset 0 -5px 12px rgba(255, 255, 255, 0.6),
    inset 0 5px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.message-wall:hover {
  box-shadow: 
    0 15px 30px rgba(0, 0, 0, 0.1),
    0 8px 10px rgba(0, 0, 0, 0.15),
    inset 0 -5px 12px rgba(255, 255, 255, 0.6),
    inset 0 5px 12px rgba(0, 0, 0, 0.05);
}

.danmaku-container {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
}

.message-item {
  background-color: white;
  padding: 10px 20px;
  border-radius: 24px;
  display: inline-block;
  position: absolute;
  box-shadow: 
    0 3px 8px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.8);
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  will-change: transform, left;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  font-size: 14px;
  color: #333;
  z-index: 1;
  animation-name: moveRightToLeft;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  left: 100%; /* 初始位置在右侧边缘外 */
  opacity: 0; /* 初始时不可见 */
  animation-play-state: paused; /* 初始时暂停动画 */
}

.message-item:hover {
  transform: translateY(-3px) scale(1.03);
  box-shadow: 
    0 6px 16px rgba(0, 0, 0, 0.12),
    0 3px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 1px rgba(255, 255, 255, 0.8);
  z-index: 10;
  background-color: #fafcff;
  animation-play-state: paused;
}

.approval-tag {
  display: inline-block;
  padding: 2px 6px;
  margin-right: 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
}

.approval-tag.approved {
  background-color: #67C23A;
  color: white;
}

.approval-tag.pending {
  background-color: #E6A23C;
  color: white;
}

@keyframes moveRightToLeft {
  0% {
    left: 100%;
    opacity: 0;
  }
  1% {
    opacity: 1; /* 动画开始时变为可见 */
  }
  100% {
    left: -100%;
    opacity: 1;
  }
}

/* 在Vue挂载后，启动所有动画 */
.danmaku-container:not(:empty) .message-item {
  animation-play-state: running;
}

.input-area {
  display: flex;
  margin-top: 30px;
  position: relative;
  justify-content: center;
  align-items: center;
}

.input-wrapper {
  position: relative;
  flex: 1;
  max-width: 600px;
}

.message-input {
  width: 100%;
  padding: 14px 20px;
  padding-right: 50px;
  border: 1px solid #e0e3e9;
  border-radius: 30px;
  outline: none;
  transition: all 0.3s;
  font-size: 15px;
  box-shadow: 
    0 2px 6px rgba(0, 0, 0, 0.04),
    inset 0 1px 2px rgba(0, 0, 0, 0.03);
  color: #333;
}

.message-input:focus {
  border-color: #409eff;
  box-shadow: 
    0 0 0 3px rgba(64, 158, 255, 0.2),
    inset 0 1px 2px rgba(0, 0, 0, 0.03);
}

.char-count {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #999;
}

.send-button, .my-messages-button {
  padding: 0;
  margin-left: 12px;
  height: 48px;
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  font-weight: 500;
  font-size: 15px;
}

.send-button {
  width: 100px;
  background: linear-gradient(135deg, #409eff, #3a8ee6);
  box-shadow: 
    0 4px 10px rgba(58, 142, 230, 0.3),
    0 1px 3px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

.my-messages-button {
  width: 80px;
  background: linear-gradient(135deg, #67C23A, #529b2e);
  box-shadow: 
    0 4px 10px rgba(103, 194, 58, 0.3),
    0 1px 3px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

.send-button:hover:not(:disabled), .my-messages-button:hover {
  transform: translateY(-2px);
}

.send-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #4dabff, #4095f0);
  box-shadow: 
    0 6px 15px rgba(58, 142, 230, 0.4),
    0 2px 5px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.3);
}

.my-messages-button:hover {
  background: linear-gradient(135deg, #85ce61, #67c23a);
  box-shadow: 
    0 6px 15px rgba(103, 194, 58, 0.4),
    0 2px 5px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.3);
}

.send-button:active:not(:disabled), .my-messages-button:active {
  transform: translateY(1px);
}

.send-button:active:not(:disabled) {
  box-shadow: 
    0 2px 5px rgba(58, 142, 230, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
}

.my-messages-button:active {
  box-shadow: 
    0 2px 5px rgba(103, 194, 58, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
}

.send-button:disabled {
  background: linear-gradient(135deg, #a0cfff, #9ec8f5);
  cursor: not-allowed;
  opacity: 0.7;
}

.button-text {
  position: relative;
  z-index: 2;
  font-weight: 500;
  font-size: 15px;
  letter-spacing: 0.5px;
}

/* 我的留言样式 */
.my-messages-container {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.my-messages-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 10px;
}

.no-messages {
  text-align: center;
  color: #909399;
  padding: 30px 0;
}

.my-message-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
}

.my-message-item:last-child {
  border-bottom: none;
}

.message-content {
  flex: 1;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.message-content span {
  margin-right: 10px;
}

.message-date {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}

.message-actions {
  display: flex;
  gap: 10px;
}

.action-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  transition: all 0.3s;
}

.action-button.edit {
  background-color: #e6f7ff;
  color: #1890ff;
}

.action-button.edit:hover {
  background-color: #bae7ff;
}

.action-button.delete {
  background-color: #fff1f0;
  color: #f5222d;
}

.action-button.delete:hover {
  background-color: #ffccc7;
}

.action-button i {
  margin-right: 4px;
}

/* 小果笔记和用户投稿区域 */
.notes-section {
  margin: 40px auto 20px;
  text-align: left;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 80%;
}

.notes-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
  text-align: left;
}

.blog-card {
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
  width: 50%;
}

.blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.blog-content {
  display: flex;
  align-items: center;
  text-align: left;
}

.blog-avatar {
  width: 80px;
  height: 80px;
  margin-right: 20px;
  overflow: hidden;
  border-radius: 12px;
  flex-shrink: 0;
}

.blog-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-info {
  flex: 1;
}

.blog-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.blog-description {
  font-size: 1.1rem;
  color: #606266;
  margin: 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .xiaoguo-square {
    width: 95%;
    padding: 15px 0;
  }

  .notes-section {
    width: 100%;
  }

  .blog-card {
    width: 90%;
  }
  
  .blog-content {
    flex-direction: column;
    text-align: center;
  }
  
  .blog-avatar {
    margin-right: 0;
    margin-bottom: 15px;
  }

  .message-wall {
    border-radius: 50px; 
    padding: 15px;
    height: 140px;
  }

  .input-area {
    flex-direction: column;
    align-items: center;
  }

  .input-wrapper {
    width: 100%;
    max-width: none;
  }

  .send-button, .my-messages-button {
    margin-left: 0;
    margin-top: 12px;
    width: 48%;
    height: 44px;
  }
  
  .send-button {
    margin-right: 4px;
  }
  
  .my-messages-button {
    margin-left: 4px;
  }
  
  .my-message-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .message-actions {
    margin-top: 10px;
    align-self: flex-end;
  }
}
</style> 